import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, RefreshCw, ChevronUp, ChevronDown, Eye, Clock, TrendingDown, UserX, AlertCircle, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import GymDrilldownModal from '@/components/GymDrilldownModal';
import { useWtfLiveData } from '../hooks/useWtfLiveData';
import { WtfDataColumn, WtfSyncBadge } from '../components/wtf';

// ProblemBadges component (same as CommandCenter.js)
const ProblemBadges = ({ problems = [] }) => {
  const badgeConfig = {
    missed_pulse: { icon: Clock, label: 'Missed', className: 'bg-red-100 text-red-700 border-red-200' },
    no_fd: { icon: UserX, label: 'No FD', className: 'bg-orange-100 text-orange-700 border-orange-200' },
    low_connect: { icon: TrendingDown, label: 'Low %', className: 'bg-red-100 text-red-700 border-red-200' },
    pending_verification: { icon: AlertCircle, label: 'Pending', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' }
  };

  if (!problems || problems.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {problems.map(p => {
        const config = badgeConfig[p];
        if (!config) return null;
        const Icon = config.icon;
        return (
          <Badge key={p} variant="outline" className={`text-xs py-0 ${config.className}`}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        );
      })}
    </div>
  );
};

// Sort icon component
const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <ChevronUp className="w-3 h-3 text-gray-300" />;
  }
  return sortConfig.direction === 'asc'
    ? <ChevronUp className="w-3 h-3 text-blue-600" />
    : <ChevronDown className="w-3 h-3 text-blue-600" />;
};

// Format currency in Indian format
const formatCurrency = (amount) => {
  if (!amount || amount === 0) return '₹0';
  return '₹' + amount.toLocaleString('en-IN');
};

// Get array of dates between start and end
const getDateRange = (startDate, endDate) => {
  const dates = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  while (start <= end) {
    dates.push(start.toISOString().split('T')[0]);
    start.setDate(start.getDate() + 1);
  }
  return dates;
};

export default function GymPerformance() {
  // State - Date range instead of single date
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use IST date to match backend Asia/Kolkata timezone (UTC date is wrong between 00:00-05:30 IST)
  const today = (() => { const now = new Date(); return new Date(now.getTime() + 5.5 * 60 * 60 * 1000).toISOString().split('T')[0]; })();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [sortConfig, setSortConfig] = useState({ key: 'closures', direction: 'desc' });
  const [filters, setFilters] = useState({ manager: 'all', tl: 'all', gym: 'all' });
  const [drilldownModal, setDrilldownModal] = useState({ open: false, gymId: null });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [dateRangeDays, setDateRangeDays] = useState(1);

  // User info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'team_leader';

  // WTF Live Data hooks — only targets feed has per-gym data
  const { data: wtfTargets, lastSync: wtfSync, stale: wtfStale, loading: wtfLoading } = useWtfLiveData('targets');

  // Build per-gym WTF lookup from targets feed
  // targets feed: [{gym_id, gym_name, daily_target, daily_achieved, remaining_target, target_date}]
  // All values are strings — parseInt them
  const wtfGymData = useMemo(() => {
    const lookup = {};
    const targetsArr = Array.isArray(wtfTargets) ? wtfTargets : [];
    targetsArr.forEach(item => {
      const key = item.gym_id || item.gym_name;
      if (key) {
        const target = parseInt(item.daily_target || '0', 10);
        const achieved = parseInt(item.daily_achieved || '0', 10);
        const remaining = parseInt(item.remaining_target || '0', 10);
        lookup[key] = {
          target,
          achieved,
          remaining,
          achievement: target > 0 ? Math.round((achieved / target) * 100) : null,
        };
        // Also index by gym_name for name-based matching
        if (item.gym_name && item.gym_name !== key) {
          lookup[item.gym_name] = lookup[key];
        }
      }
    });
    return lookup;
  }, [wtfTargets]);

  // Aggregate gym data from multiple days
  const aggregateGymData = useCallback((responses) => {
    const gymMap = new Map();

    responses.forEach(dayData => {
      if (!dayData?.gym_performance) return;

      dayData.gym_performance.forEach(gym => {
        const gymId = gym.gym_id;
        if (!gymMap.has(gymId)) {
          gymMap.set(gymId, {
            gym: gym.gym,
            gym_id: gymId,
            tl_name: gym.tl_name,
            manager_name: gym.manager_name,
            dialed: 0,
            connected: 0,
            closures: 0,
            closure_expected: 0,
            closure_done: 0,
            commitment_amount: 0,
            commitment_amount_fulfilled: 0,
            commitment_count: 0,
            commitment_fulfilled: 0,
            problems: [],
            pulses_filled: 0,
            total_pulses: 0,
            p1_count: 0,
            p2_count: 0,
            p3_count: 0,
            p4_count: 0,
            days_count: 0
          });
        }

        const agg = gymMap.get(gymId);
        agg.dialed += gym.dialed || 0;
        agg.connected += gym.connected || 0;
        agg.closures += gym.closures || 0;
        agg.closure_expected += gym.closure_expected || 0;
        agg.closure_done += gym.closure_done || 0;
        agg.commitment_amount += gym.commitment_amount || 0;
        agg.commitment_amount_fulfilled += gym.commitment_amount_fulfilled || 0;
        agg.commitment_count += gym.commitment_count || 0;
        agg.commitment_fulfilled += gym.commitment_fulfilled || 0;
        agg.pulses_filled += gym.pulses_filled || 0;
        agg.total_pulses += gym.total_pulses || 4;
        agg.p1_count += gym.p1 ? 1 : 0;
        agg.p2_count += gym.p2 ? 1 : 0;
        agg.p3_count += gym.p3 ? 1 : 0;
        agg.p4_count += gym.p4 ? 1 : 0;
        agg.days_count += 1;

        // Merge problems (unique)
        if (gym.problems) {
          gym.problems.forEach(p => {
            if (!agg.problems.includes(p)) agg.problems.push(p);
          });
        }
      });
    });

    // Calculate derived fields
    const result = [];
    gymMap.forEach(gym => {
      const days = gym.days_count || 1;
      result.push({
        ...gym,
        connect_pct: gym.dialed > 0 ? Math.round((gym.connected / gym.dialed) * 1000) / 10 : 0,
        fill_rate: gym.total_pulses > 0 ? Math.round((gym.pulses_filled / gym.total_pulses) * 100) : 0,
        p1: gym.p1_count >= days * 0.5, // Consider filled if at least half the days
        p2: gym.p2_count >= days * 0.5,
        p3: gym.p3_count >= days * 0.5,
        p4: gym.p4_count >= days * 0.5
      });
    });

    return result;
  }, []);

  // Fetch data for date range
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const dates = getDateRange(startDate, endDate);
      setDateRangeDays(dates.length);

      // Fetch data for all dates in parallel
      const responses = await Promise.all(
        dates.map(date =>
          axiosInstance.get(`/command-center?date=${date}`)
            .then(res => res.data)
            .catch(() => null) // Handle individual date failures gracefully
        )
      );

      // Filter out null responses
      const validResponses = responses.filter(r => r !== null);

      if (validResponses.length === 0) {
        toast.error('No data found for selected date range');
        setData({ gym_performance: [], targets: {} });
      } else {
        // Aggregate the data
        const aggregatedGyms = aggregateGymData(validResponses);

        // Get targets from the first valid response
        const targets = validResponses[0]?.targets || {};

        setData({
          gym_performance: aggregatedGyms,
          targets
        });
      }

      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to load gym performance data');
      console.error('Error fetching gym performance:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, aggregateGymData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Extract gym performance data
  const gymPerformance = data?.gym_performance || [];
  const targets = data?.targets || {};

  // Apply role-based scoping
  const scopedData = useMemo(() => {
    let result = gymPerformance;

    // TL sees only their own gyms
    if (userRole === 'team_leader' && user?.assigned_gyms) {
      result = result.filter(g => user.assigned_gyms.includes(g.gym_id));
    }

    // Manager sees only their team's gyms
    if (userRole === 'manager' && user?.assigned_gyms) {
      result = result.filter(g => user.assigned_gyms.includes(g.gym_id));
    }

    // Admin sees all gyms (no filter)
    return result;
  }, [gymPerformance, userRole, user]);

  // Extract unique filter options from scoped data
  const filterOptions = useMemo(() => {
    const managers = [...new Set(scopedData.map(g => g.manager_name).filter(Boolean))].sort();
    const tls = [...new Set(scopedData.map(g => g.tl_name).filter(Boolean))].sort();
    const gyms = [...new Set(scopedData.map(g => g.gym).filter(Boolean))].sort();
    return { managers, tls, gyms };
  }, [scopedData]);

  // Apply dropdown filters
  const filteredData = useMemo(() => {
    let result = scopedData;

    if (filters.manager !== 'all') {
      result = result.filter(g => g.manager_name === filters.manager);
    }
    if (filters.tl !== 'all') {
      result = result.filter(g => g.tl_name === filters.tl);
    }
    if (filters.gym !== 'all') {
      result = result.filter(g => g.gym === filters.gym);
    }

    return result;
  }, [scopedData, filters]);

  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      const key = sortConfig.key;
      let aVal = a[key];
      let bVal = b[key];

      // Handle numeric fields
      if (['dialed', 'connected', 'connect_pct', 'closures', 'closure_expected', 'closure_done',
           'commitment_amount', 'commitment_amount_fulfilled', 'fill_rate', 'pulses_filled'].includes(key)) {
        aVal = aVal || 0;
        bVal = bVal || 0;
      }

      // Handle string fields
      if (['gym', 'tl_name', 'manager_name'].includes(key)) {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      // Handle computed fields
      if (key === 'target') {
        aVal = targets.per_gym_closure_target || 5;
        bVal = targets.per_gym_closure_target || 5;
      }
      if (key === 'gap') {
        const target = targets.per_gym_closure_target || 5;
        aVal = (a.closures || 0) - target;
        bVal = (b.closures || 0) - target;
      }

      // Handle WTF fields (lookup from wtfGymData)
      if (key === 'wtf_target') {
        aVal = (wtfGymData[a.gym_id] || wtfGymData[a.gym] || {}).target || 0;
        bVal = (wtfGymData[b.gym_id] || wtfGymData[b.gym] || {}).target || 0;
      }
      if (key === 'wtf_achieved') {
        aVal = (wtfGymData[a.gym_id] || wtfGymData[a.gym] || {}).achieved || 0;
        bVal = (wtfGymData[b.gym_id] || wtfGymData[b.gym] || {}).achieved || 0;
      }
      if (key === 'wtf_remaining') {
        aVal = (wtfGymData[a.gym_id] || wtfGymData[a.gym] || {}).remaining || 0;
        bVal = (wtfGymData[b.gym_id] || wtfGymData[b.gym] || {}).remaining || 0;
      }
      if (key === 'wtf_achievement') {
        aVal = (wtfGymData[a.gym_id] || wtfGymData[a.gym] || {}).achievement || 0;
        bVal = (wtfGymData[b.gym_id] || wtfGymData[b.gym] || {}).achievement || 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredData, sortConfig, targets, wtfGymData]);

  // Handle column header click for sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // CSV Export - updated with commitment amount columns
  const exportToCSV = () => {
    const headers = [
      'Gym', 'TL', 'Manager', 'Dialed', 'Connected', 'Connect %', 'Closures',
      'Commitment Count', 'Commitment Fulfilled', 'Commitment Amount (₹)', 'Commitment Fulfilled (₹)',
      'Target', 'Gap', 'Status', 'P1', 'P2', 'P3', 'P4', 'Fill Rate'
    ];
    const target = (targets.per_gym_closure_target || 5) * dateRangeDays;
    const rows = sortedData.map(g => {
      const gap = (g.closures || 0) - target;
      const connectPct = g.connect_pct || 0;
      let status = 'Met';
      if ((g.closures || 0) === 0 || connectPct < 60) status = 'Critical';
      else if (connectPct < 70 || gap < -10) status = 'Warning';
      return [
        g.gym,
        g.tl_name || '-',
        g.manager_name || '-',
        g.dialed || 0,
        g.connected || 0,
        `${connectPct}%`,
        g.closures || 0,
        g.closure_expected || 0,
        `${g.closure_done || 0}/${g.closure_expected || 0}`,
        g.commitment_amount || 0,
        g.commitment_amount_fulfilled || 0,
        target,
        gap,
        status,
        g.p1 != null ? (g.p1 ? 'Yes' : 'No') : '-',
        g.p2 != null ? (g.p2 ? 'Yes' : 'No') : '-',
        g.p3 != null ? (g.p3 ? 'Yes' : 'No') : '-',
        g.p4 != null ? (g.p4 ? 'Yes' : 'No') : '-',
        g.pulses_filled != null ? `${g.pulses_filled}/${g.total_pulses || 4} (${g.fill_rate || 0}%)` : '-'
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const filename = startDate === endDate
      ? `gym-performance-${startDate}.csv`
      : `gym-performance-${startDate}-to-${endDate}.csv`;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} gyms to CSV`);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({ manager: 'all', tl: 'all', gym: 'all' });
  };

  // Determine which filters to show based on role
  const showManagerFilter = userRole === 'admin';
  const showTLFilter = userRole === 'admin' || userRole === 'manager';
  const showGymFilter = true; // All roles can filter by gym within their scope

  // Calculate totals for summary
  const totals = useMemo(() => {
    return sortedData.reduce((acc, gym) => ({
      commitment_amount: acc.commitment_amount + (gym.commitment_amount || 0),
      commitment_amount_fulfilled: acc.commitment_amount_fulfilled + (gym.commitment_amount_fulfilled || 0),
      closure_expected: acc.closure_expected + (gym.closure_expected || 0),
      closure_done: acc.closure_done + (gym.closure_done || 0)
    }), { commitment_amount: 0, commitment_amount_fulfilled: 0, closure_expected: 0, closure_done: 0 });
  }, [sortedData]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#1F4E79]">Gym Performance</h1>
            <WtfSyncBadge lastSync={wtfSync} stale={wtfStale} />
          </div>
          <p className="text-sm text-gray-500">
            Last updated: {lastUpdate.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}
            {dateRangeDays > 1 && (
              <span className="ml-2 text-blue-600">({dateRangeDays} days aggregated)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1 border">
            <Calendar className="w-4 h-4 text-gray-400 ml-2" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (e.target.value > endDate) setEndDate(e.target.value);
              }}
              className="w-36 border-0 bg-transparent"
            />
            <span className="text-gray-400">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (e.target.value < startDate) setStartDate(e.target.value);
              }}
              className="w-36 border-0 bg-transparent"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchData}
            disabled={loading}
            title="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards - Commitment Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3 px-4">
            <div className="text-sm text-blue-600 font-medium">Commitment Amount</div>
            <div className="text-xl font-bold text-blue-800">{formatCurrency(totals.commitment_amount)}</div>
            <div className="text-xs text-blue-500">{totals.closure_expected} commitments</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-3 px-4">
            <div className="text-sm text-green-600 font-medium">Amount Fulfilled</div>
            <div className="text-xl font-bold text-green-800">{formatCurrency(totals.commitment_amount_fulfilled)}</div>
            <div className="text-xs text-green-500">{totals.closure_done}/{totals.closure_expected} fulfilled</div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="py-3 px-4">
            <div className="text-sm text-orange-600 font-medium">Pending Amount</div>
            <div className="text-xl font-bold text-orange-800">
              {formatCurrency(totals.commitment_amount - totals.commitment_amount_fulfilled)}
            </div>
            <div className="text-xs text-orange-500">{totals.closure_expected - totals.closure_done} pending</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="py-3 px-4">
            <div className="text-sm text-purple-600 font-medium">Fulfillment Rate</div>
            <div className="text-xl font-bold text-purple-800">
              {totals.commitment_amount > 0
                ? Math.round((totals.commitment_amount_fulfilled / totals.commitment_amount) * 100)
                : 0}%
            </div>
            <div className="text-xs text-purple-500">by amount</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters Row */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            {showManagerFilter && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Manager:</span>
                <Select value={filters.manager} onValueChange={(v) => setFilters(prev => ({ ...prev, manager: v }))}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="All Managers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Managers</SelectItem>
                    {filterOptions.managers.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showTLFilter && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">TL:</span>
                <Select value={filters.tl} onValueChange={(v) => setFilters(prev => ({ ...prev, tl: v }))}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="All TLs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All TLs</SelectItem>
                    {filterOptions.tls.map(tl => (
                      <SelectItem key={tl} value={tl}>{tl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showGymFilter && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Gym:</span>
                <Select value={filters.gym} onValueChange={(v) => setFilters(prev => ({ ...prev, gym: v }))}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Gyms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Gyms</SelectItem>
                    {filterOptions.gyms.map(gym => (
                      <SelectItem key={gym} value={gym}>{gym}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex-1" />

            <Button variant="ghost" size="sm" onClick={resetFilters} className="text-gray-500">
              Reset Filters
            </Button>

            <Button variant="outline" size="sm" onClick={exportToCSV} className="flex items-center gap-1">
              <Download className="w-4 h-4" />
              Export CSV
            </Button>

            <Badge variant="secondary" className="ml-2">
              {sortedData.length} gyms
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Gym Performance Table */}
      <Card>
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base">GYM PERFORMANCE & COMMITMENT TRACKING</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading gym performance data...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#D6DCE5]">
                  <tr>
                    {[
                      { key: 'gym', label: 'Gym', align: 'left' },
                      { key: 'tl_name', label: 'TL', align: 'left' },
                      { key: 'manager_name', label: 'Manager', align: 'left' },
                      { key: 'dialed', label: 'Dialed', align: 'center' },
                      { key: 'connected', label: 'Connected', align: 'center' },
                      { key: 'connect_pct', label: 'Connect %', align: 'center' },
                      { key: 'closures', label: 'Closures', align: 'center' },
                      { key: 'closure_expected', label: 'Commitments', align: 'center' },
                      { key: 'closure_done', label: 'Fulfilled', align: 'center' },
                      { key: 'commitment_amount', label: 'Amount (₹)', align: 'center' },
                      { key: 'commitment_amount_fulfilled', label: 'Fulfilled (₹)', align: 'center' },
                      { key: 'target', label: 'Target', align: 'center' },
                      { key: 'gap', label: 'Gap', align: 'center' },
                      { key: 'status', label: 'Status', align: 'center' },
                      { key: 'issues', label: 'Issues', align: 'center', sortable: false },
                      { key: 'p1', label: 'P1', align: 'center' },
                      { key: 'p2', label: 'P2', align: 'center' },
                      { key: 'p3', label: 'P3', align: 'center' },
                      { key: 'p4', label: 'P4', align: 'center' },
                      { key: 'fill_rate', label: 'Fill Rate', align: 'center' },
                      { key: 'wtf_target', label: 'WTF Target', align: 'center', wtf: true },
                      { key: 'wtf_achieved', label: 'WTF Achieved', align: 'center', wtf: true },
                      { key: 'wtf_remaining', label: 'WTF Remaining', align: 'center', wtf: true },
                      { key: 'wtf_achievement', label: 'Achievement %', align: 'center', wtf: true }
                    ].map(col => (
                      <th
                        key={col.key}
                        className={`p-2 border font-bold ${col.align === 'center' ? 'text-center' : 'text-left'} ${col.sortable !== false ? 'cursor-pointer hover:bg-[#C6CCD5] select-none' : ''} ${col.wtf ? 'text-purple-600 bg-purple-50' : ''}`}
                        onClick={() => col.sortable !== false && handleSort(col.key)}
                      >
                        <div className={`flex items-center gap-1 ${col.align === 'center' ? 'justify-center' : ''}`}>
                          {col.label}
                          {col.sortable !== false && <SortIcon column={col.key} sortConfig={sortConfig} />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.length === 0 ? (
                    <tr>
                      <td colSpan={24} className="p-8 text-center text-gray-500">
                        No gyms found matching the current filters.
                      </td>
                    </tr>
                  ) : (
                    sortedData.map((gym, idx) => {
                      const target = (targets.per_gym_closure_target || 5) * dateRangeDays;
                      const gap = (gym.closures || 0) - target;
                      const connectPct = gym.connect_pct || 0;
                      let status = '✅';
                      if ((gym.closures || 0) === 0 || connectPct < 60) status = '🔴';
                      else if (connectPct < 70 || gap < -10) status = '🟡';

                      // Calculate amount fulfillment percentage for color
                      const amtFulfillPct = gym.commitment_amount > 0
                        ? (gym.commitment_amount_fulfilled / gym.commitment_amount) * 100
                        : 0;

                      return (
                        <tr
                          key={gym.gym_id || idx}
                          className={`${(gym.closures || 0) === 0 ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 cursor-pointer transition-colors`}
                          onClick={() => setDrilldownModal({ open: true, gymId: gym.gym_id })}
                          data-testid={`gym-row-${gym.gym_id}`}
                        >
                          <td className="p-2 border font-semibold text-blue-600 hover:underline">
                            <div className="flex items-center gap-1">
                              {gym.gym}
                              <Eye size={14} className="text-gray-400" />
                            </div>
                          </td>
                          <td className="p-2 border text-gray-600">{gym.tl_name || '-'}</td>
                          <td className="p-2 border text-gray-600">{gym.manager_name || '-'}</td>
                          <td className="p-2 border text-center">{gym.dialed || 0}</td>
                          <td className="p-2 border text-center">{gym.connected || 0}</td>
                          <td className="p-2 border text-center font-medium" style={{ color: connectPct >= 70 ? '#28A745' : connectPct >= 60 ? '#FFC107' : '#DC3545' }}>
                            {connectPct}%
                          </td>
                          <td className="p-2 border text-center font-semibold text-green-700">{gym.closures || 0}</td>
                          <td className="p-2 border text-center">{gym.closure_expected || 0}</td>
                          <td className="p-2 border text-center font-medium" style={{
                            color: (gym.closure_done || 0) >= (gym.closure_expected || 1)
                              ? '#28A745'
                              : (gym.closure_done || 0) > 0 ? '#FFC107' : '#DC3545'
                          }}>
                            {gym.closure_done || 0}/{gym.closure_expected || 0}
                          </td>
                          <td className="p-2 border text-center font-medium text-blue-700">
                            {formatCurrency(gym.commitment_amount || 0)}
                          </td>
                          <td className="p-2 border text-center font-medium" style={{
                            color: amtFulfillPct >= 100 ? '#28A745' : amtFulfillPct > 0 ? '#FFC107' : '#DC3545'
                          }}>
                            {formatCurrency(gym.commitment_amount_fulfilled || 0)}
                          </td>
                          <td className="p-2 border text-center text-gray-500">{target}</td>
                          <td className={`p-2 border text-center font-medium ${gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {gap >= 0 ? '+' : ''}{gap}
                          </td>
                          <td className="p-2 border text-center text-xl">{status}</td>
                          <td className="p-2 border">
                            <ProblemBadges problems={gym.problems} />
                          </td>
                          <td className="p-2 border text-center">{gym.p1 != null ? (gym.p1 ? '\u2705' : '\u23F3') : '-'}</td>
                          <td className="p-2 border text-center">{gym.p2 != null ? (gym.p2 ? '\u2705' : '\u23F3') : '-'}</td>
                          <td className="p-2 border text-center">{gym.p3 != null ? (gym.p3 ? '\u2705' : '\u23F3') : '-'}</td>
                          <td className="p-2 border text-center">{gym.p4 != null ? (gym.p4 ? '\u2705' : '\u23F3') : '-'}</td>
                          <td className="p-2 border text-center font-medium" style={{
                            color: (gym.fill_rate || 0) === 100 ? '#28A745' :
                                   (gym.fill_rate || 0) >= 50 ? '#FFC107' : '#DC3545'
                          }}>
                            {gym.pulses_filled != null ? `${gym.pulses_filled}/${gym.total_pulses || 4} (${gym.fill_rate || 0}%)` : '-'}
                          </td>
                          {/* WTF Live Data Columns */}
                          {(() => {
                            const wtf = wtfGymData[gym.gym_id] || wtfGymData[gym.gym] || {};
                            const achievementColor = wtf.achievement == null ? 'text-gray-400'
                              : wtf.achievement >= 90 ? 'text-green-600 font-bold'
                              : wtf.achievement >= 70 ? 'text-yellow-600 font-semibold'
                              : 'text-red-600 font-bold';
                            return (
                              <>
                                <td className="p-2 border text-center">
                                  <WtfDataColumn value={wtf.target} loading={wtfLoading} stale={wtfStale} format="number" />
                                </td>
                                <td className="p-2 border text-center">
                                  <WtfDataColumn value={wtf.achieved} loading={wtfLoading} stale={wtfStale} format="number" />
                                </td>
                                <td className="p-2 border text-center">
                                  <WtfDataColumn value={wtf.remaining} loading={wtfLoading} stale={wtfStale} format="number" />
                                </td>
                                <td className={`p-2 border text-center ${achievementColor}`}>
                                  {wtf.achievement != null ? `${wtf.achievement}%` : '--'}
                                </td>
                              </>
                            );
                          })()}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gym Drilldown Modal */}
      <GymDrilldownModal
        gymId={drilldownModal.gymId}
        open={drilldownModal.open}
        onClose={() => setDrilldownModal({ open: false, gymId: null })}
        date={endDate}
      />
    </div>
  );
}
